// /admin/groups — every group on the site, with the two site-level actions.
//
// This screen carries more weight than it looks like it should. Since site role
// stopped granting anything inside a group (2026-08-21), an admin has no way to
// even discover that a group exists except here. Suspending and deleting are
// powers over the CONTAINER, not over its contents: there is deliberately no
// "edit this group" or "read this group's feed" action, because that would be
// group authority smuggled back in through the admin portal. To act inside a
// group, an admin masquerades.

import {
  ApiClientError,
  adminListGroups,
  adminUpdateGroup,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  TimeFormat,
  fmtInstant,
  type AdminGroupOrder,
  type AdminGroupRow,
  type AdminGroupStatus,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { adminError, adminSubnav, rowActions } from './adminNav.js';

const STATUSES: Array<{ value: AdminGroupStatus; label: string }> = [
  { value: 'live', label: 'Live' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'all', label: 'All' },
];

const ORDERS: Array<{ value: AdminGroupOrder; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'newest', label: 'Newest' },
  { value: 'members', label: 'Most members' },
  { value: 'events', label: 'Most events' },
];

export const adminGroupsView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let zone: string;
  try {
    zone = await viewerZone();
  } catch {
    zone = 'UTC';
  }

  let status: AdminGroupStatus = 'live';
  let order: AdminGroupOrder = 'name';
  let search = '';

  const tableHost = el('div', {}, [el('p', { class: 'muted' }, 'Loading')]);
  const feedback = el('p', { class: 'form-error', hidden: true });

  const searchBox = el('input', {
    id: 'admin-group-search',
    name: 'q',
    type: 'search',
    placeholder: 'Name or slug',
  }) as HTMLInputElement;

  const statusSelect = el(
    'select',
    { id: 'admin-group-status', name: 'status' },
    STATUSES.map((s) => el('option', { value: s.value }, s.label)),
  ) as HTMLSelectElement;

  const orderSelect = el(
    'select',
    { id: 'admin-group-order', name: 'order' },
    ORDERS.map((o) => el('option', { value: o.value }, o.label)),
  ) as HTMLSelectElement;

  async function load(): Promise<void> {
    feedback.hidden = true;
    try {
      const rows = await adminListGroups({ q: search, status, order });
      tableHost.replaceChildren(rows.length ? table(rows) : empty(status));
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      main.replaceChildren(
        el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
        adminSubnav('groups'),
        ...adminError(err instanceof ApiClientError ? err.status : undefined),
      );
    }
  }

  /** Apply one change and swap just that row, so the filter and scroll survive. */
  async function act(row: AdminGroupRow, changes: { isSuspended?: boolean; isDeleted?: boolean }): Promise<void> {
    feedback.hidden = true;
    try {
      await adminUpdateGroup(row.groupId, changes);
      // Re-load rather than patch in place: a change can move the row out of the
      // current filter entirely (suspending while viewing Live), and leaving it
      // sitting there would misreport what the filter means.
      await load();
    } catch (err) {
      feedback.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      feedback.hidden = false;
    }
  }

  function table(rows: AdminGroupRow[]): HTMLElement {
    return el('table', { class: 'table' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', {}, 'Group'),
          el('th', {}, 'Owner'),
          el('th', {}, 'Members'),
          el('th', {}, 'Events'),
          el('th', {}, 'Created'),
          el('th', {}, 'State'),
          el('th', {}, 'Actions'),
        ]),
      ]),
      el(
        'tbody',
        {},
        rows.map((g) =>
          el('tr', {}, [
            el('td', {}, [
              el('strong', {}, g.name),
              el('br', {}),
              el('span', { class: 'muted small' }, g.slug),
            ]),
            // The owner's account may be gone even though the group is not: the
            // FK has no cascade on purpose.
            el('td', {}, g.ownerName || el('span', { class: 'muted' }, `#${g.ownerId}`)),
            el('td', {}, String(g.memberCount)),
            el('td', {}, String(g.eventCount)),
            el('td', {}, fmtInstant(g.createdAt, zone, TimeFormat.SQL_DATE) ?? ''),
            el('td', {}, stateBadges(g)),
            el('td', {}, actions(g)),
          ]),
        ),
      ),
    ]);
  }

  /**
   * Both flags, not one derived label. Suspended and deleted are independent
   * states and a group can be in both; collapsing them into one word would hide
   * the fact that un-deleting leaves it still suspended.
   */
  function stateBadges(g: AdminGroupRow): HTMLElement[] {
    const badges: HTMLElement[] = [];
    if (g.isDeleted) badges.push(el('span', { class: 'badge badge-danger' }, 'Deleted'));
    if (g.isSuspended) badges.push(el('span', { class: 'badge badge-suspended' }, 'Suspended'));
    if (!badges.length) badges.push(el('span', { class: 'badge badge-success' }, 'Live'));
    return badges;
  }

  function actions(g: AdminGroupRow): HTMLElement[] {
    const out: HTMLElement[] = [];

    const suspendBtn = el(
      'button',
      { type: 'button', class: 'btn btn-link' },
      g.isSuspended ? 'Restore' : 'Suspend',
    );
    suspendBtn.addEventListener('click', () => {
      // Confirming only on the way IN. Suspending stops a live group for
      // everyone in it; lifting it is the safe direction and does not need a
      // speed bump.
      if (
        !g.isSuspended &&
        !confirm(
          `Suspend "${g.name}"?\n\nIts ${g.memberCount} member(s) keep read access to ` +
            `everything, but nobody can post, upload, RSVP, or create events, and its ` +
            `announcements and day-of pings stop. Reversible.`,
        )
      ) {
        return;
      }
      void act(g, { isSuspended: !g.isSuspended });
    });
    out.push(suspendBtn);

    const deleteBtn = el(
      'button',
      { type: 'button', class: 'btn btn-link btn-danger' },
      g.isDeleted ? 'Undelete' : 'Delete',
    );
    deleteBtn.addEventListener('click', () => {
      if (
        !g.isDeleted &&
        !confirm(
          `Delete "${g.name}"?\n\nThe group disappears for its ${g.memberCount} member(s) ` +
            `and its events stop being reachable. This is a soft delete, so it can be ` +
            `undone from the Deleted filter.`,
        )
      ) {
        return;
      }
      void act(g, { isDeleted: !g.isDeleted });
    });
    out.push(deleteBtn);

    return rowActions(out);
  }

  // method="post" even though JS handles it: if the script fails, the browser
  // must not fall back to GET and put the filter in the URL as a bare query.
  const filters = el('form', { method: 'post', class: 'filter-bar', novalidate: true }, [
    el('label', { for: 'admin-group-search' }, 'Search'),
    searchBox,
    el('label', { for: 'admin-group-status' }, 'State'),
    statusSelect,
    el('label', { for: 'admin-group-order' }, 'Order'),
    orderSelect,
  ]) as HTMLFormElement;

  filters.addEventListener('submit', (ev) => ev.preventDefault());

  let debounce: number | undefined;
  searchBox.addEventListener('input', () => {
    search = searchBox.value.trim();
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void load(), 250);
  });
  statusSelect.addEventListener('change', () => {
    status = statusSelect.value as AdminGroupStatus;
    void load();
  });
  orderSelect.addEventListener('change', () => {
    order = orderSelect.value as AdminGroupOrder;
    void load();
  });

  main.replaceChildren(
    el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
    adminSubnav('groups'),
    el('h2', {}, 'Groups'),
    el(
      'p',
      { class: 'muted small' },
      'Every group on the site. Suspending makes a group read-only but keeps it ' +
        'readable for its members; deleting hides it from them entirely. Both are ' +
        'reversible, and both are logged. To act INSIDE a group, masquerade as one ' +
        'of its members. Site administration no longer grants group access.',
    ),
    filters,
    feedback,
    tableHost,
  );

  await load();
};

function empty(status: AdminGroupStatus): HTMLElement {
  const msg =
    status === 'suspended'
      ? 'No suspended groups.'
      : status === 'deleted'
        ? 'No deleted groups.'
        : 'No groups match.';
  return el('p', { class: 'muted' }, msg);
}
